import { ComponentType, ReactNode, useMemo } from "react";

import { Box } from "@mui/material";
import { FormBackgroundTheme } from "@simplyk/common";

import { ThemedBackgroundShapeConfiguration } from "./useBackgroundShapesConfiguration";
import { ThemedBackgroundShapeTransform } from "./useBackgroundShapesState";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import AnimalCat from "@/icons/shapes/animal/cat.svg";
import AnimalDog from "@/icons/shapes/animal/dog.svg";
import AnimalPaw from "@/icons/shapes/animal/paw.svg";
import CulturePalette from "@/icons/shapes/culture/palette.svg";
import CultureMusicNote from "@/icons/shapes/culture/speaker.svg";
import CultureTheater from "@/icons/shapes/culture/theater.svg";
import EducationBook from "@/icons/shapes/education/book.svg";
import EducationGradcap from "@/icons/shapes/education/gradcap.svg";
import EducationPencil from "@/icons/shapes/education/pencil.svg";
import EnvironmentGlobe from "@/icons/shapes/environment/globe.svg";
import EnvironmentLeaf from "@/icons/shapes/environment/leaf.svg";
import EnvironmentTree from "@/icons/shapes/environment/tree.svg";
import GolfBall from "@/icons/shapes/golf/golf-ball.svg";
import GolfClub from "@/icons/shapes/golf/golf-club.svg";
import GolfGreen from "@/icons/shapes/golf/golf-green.svg";
import HealthCross from "@/icons/shapes/health/health.svg";
import HealthHeart from "@/icons/shapes/health/heart.svg";
import HealthMentalHealth from "@/icons/shapes/health/mentalhealth.svg";
import HealthStethoscope from "@/icons/shapes/health/stethoscope.svg";
import HumanitarianAid from "@/icons/shapes/humanitarian/aid.svg";
import HumanitarianGlobe from "@/icons/shapes/humanitarian/globe.svg";
import HumanitarianHelping from "@/icons/shapes/humanitarian/helping.svg";
import ReligionCandle from "@/icons/shapes/religion/candle.svg";
import ReligionDove from "@/icons/shapes/religion/dove.svg";
import ReligionPray from "@/icons/shapes/religion/pray.svg";
import ShapeDesktop1 from "@/icons/shapes/shape-desktop-1.svg";
import ShapeDesktop2 from "@/icons/shapes/shape-desktop-2.svg";
import ShapeDesktop3 from "@/icons/shapes/shape-desktop-3.svg";
import ShapeDesktop4 from "@/icons/shapes/shape-desktop-4.svg";
import ShapeMobile1 from "@/icons/shapes/shape-mobile-1.svg";
import ShapeMobile2 from "@/icons/shapes/shape-mobile-2.svg";
import ShapeMobile3 from "@/icons/shapes/shape-mobile-3.svg";
import ShapeMobile4 from "@/icons/shapes/shape-mobile-4.svg";

interface ShapeComponentEntry {
  id: string;
  component: ComponentType;
  isMobile: boolean;
}

type ThemedBackgroundShapesProps = {
  transforms: { [shapeId: string]: ThemedBackgroundShapeTransform };
  transition: string;
  shapes: ThemedBackgroundShapeConfiguration[];
  theme?: FormBackgroundTheme | null;
  children?: ReactNode;
  showShapes?: boolean;
  /** Renders above the solid surface fill, below SVG shapes (e.g. canvas sparkles POC). */
  backgroundTexture?: ReactNode;
  backgroundColor: string;
  shapesBackgroundColor: string;
};

export const ThemedBackgroundShapes = ({
  shapes,
  transforms,
  transition,
  theme: backgroundTheme = null,
  children,
  showShapes = false,
  backgroundTexture,
  backgroundColor,
  shapesBackgroundColor,
}: ThemedBackgroundShapesProps) => {
  const { isSmallScreen } = useMediaQuery();

  const getShapeStyle = (shapeId: string) => {
    const shapeTransform = transforms[shapeId] || {
      rotate: 0,
      translateY: 0,
      scale: 1,
    };

    const shapeConfig = shapes.find((s) => s.id === shapeId);
    const initialTransform = shapeConfig?.initialTransform ?? "";

    const animationTransform = `translateY(${shapeTransform.translateY}px) rotate(${shapeTransform.rotate}deg) scale(${shapeTransform.scale})`;

    return {
      position: "absolute",
      fill: shapesBackgroundColor,
      ...shapeConfig?.style,
      transform: `${animationTransform} ${initialTransform}`.trim(),
      opacity: shapeTransform.opacity,
      transition: `${transition}, fill 0.3s ease-in-out`,
    };
  };

  const allComponents = useMemo(() => getShapeComponents(backgroundTheme), [backgroundTheme]);
  const components = allComponents.filter((c) => (isSmallScreen ? c.isMobile : !c.isMobile));

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        zIndex: -1,
        pointerEvents: "none",
        backgroundColor,
        transition: "background-color 0.3s ease-in-out",
      }}
      data-test="background-shapes"
    >
      {backgroundTexture ? (
        <Box
          key={backgroundTheme ?? "texture"}
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            overflow: "hidden",
            "@keyframes themedTextureFadeIn": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
            animation: "themedTextureFadeIn 400ms ease-in both",
          }}
        >
          {backgroundTexture}
        </Box>
      ) : null}
      {showShapes && (
        <Box
          key={backgroundTheme ?? "abstract"}
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            "@keyframes themedShapesFadeIn": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
            animation: "themedShapesFadeIn 400ms ease-in both",
          }}
        >
          {components.map((c) => {
            const Component = c.component;

            return (
              <Box key={c.id} id={c.id} sx={getShapeStyle(c.id)} data-test={`shape-${c.id}`}>
                <Component />
              </Box>
            );
          })}
        </Box>
      )}
      {children && <Box sx={{ position: "relative", zIndex: 1, pointerEvents: "auto" }}>{children}</Box>}
    </Box>
  );
};

ThemedBackgroundShapes.displayName = "ThemedBackgroundShapes";

const ABSTRACT_COMPONENTS: ShapeComponentEntry[] = [
  { isMobile: true, component: ShapeMobile1, id: "mobileShape1" },
  { isMobile: true, component: ShapeMobile2, id: "mobileShape2" },
  { isMobile: true, component: ShapeMobile3, id: "mobileShape3" },
  { isMobile: true, component: ShapeMobile4, id: "mobileShape4" },
  { isMobile: false, component: ShapeDesktop1, id: "desktopShape1" },
  { isMobile: false, component: ShapeDesktop2, id: "desktopShape4" },
  { isMobile: false, component: ShapeDesktop3, id: "desktopShape3" },
  { isMobile: false, component: ShapeDesktop4, id: "desktopShape2" },
];

const ANIMAL_COMPONENTS: ShapeComponentEntry[] = [
  { isMobile: false, component: AnimalDog, id: "animalDesktopDog" },
  { isMobile: false, component: AnimalPaw, id: "animalDesktopPawLarge" },
  { isMobile: false, component: AnimalPaw, id: "animalDesktopPawSmall" },
  { isMobile: false, component: AnimalCat, id: "animalDesktopCat" },
  { isMobile: false, component: AnimalPaw, id: "animalDesktopPawMedium" },
  { isMobile: true, component: AnimalDog, id: "animalMobileDog" },
  { isMobile: true, component: AnimalPaw, id: "animalMobilePawLeft" },
  { isMobile: true, component: AnimalPaw, id: "animalMobilePawRight" },
  { isMobile: true, component: AnimalCat, id: "animalMobileCat" },
];

const EDUCATION_COMPONENTS: ShapeComponentEntry[] = [
  { isMobile: false, component: EducationPencil, id: "educationDesktopPencilSmall" },
  { isMobile: false, component: EducationPencil, id: "educationDesktopPencilLarge" },
  { isMobile: false, component: EducationGradcap, id: "educationDesktopGradcap1" },
  { isMobile: false, component: EducationGradcap, id: "educationDesktopGradcap2" },
  { isMobile: false, component: EducationBook, id: "educationDesktopBook" },
  { isMobile: true, component: EducationPencil, id: "educationMobilePencil" },
  { isMobile: true, component: EducationGradcap, id: "educationMobileGradcap1" },
  { isMobile: true, component: EducationGradcap, id: "educationMobileGradcap2" },
  { isMobile: true, component: EducationBook, id: "educationMobileBook" },
];

const CULTURE_AND_ARTS_COMPONENTS: ShapeComponentEntry[] = [
  { isMobile: false, component: CultureTheater, id: "cultureAndArtsDesktopTheater1" },
  { isMobile: false, component: CultureMusicNote, id: "cultureAndArtsDesktopMusicNoteTopRight" },
  { isMobile: false, component: CultureMusicNote, id: "cultureAndArtsDesktopMusicNoteBottomLeft" },
  { isMobile: false, component: CulturePalette, id: "cultureAndArtsDesktopPalette" },
  { isMobile: false, component: CultureTheater, id: "cultureAndArtsDesktopTheater2" },
  { isMobile: true, component: CultureTheater, id: "cultureAndArtsMobileTheater1" },
  { isMobile: true, component: CulturePalette, id: "cultureAndArtsMobilePalette" },
  { isMobile: true, component: CultureMusicNote, id: "cultureAndArtsMobileMusicNote" },
  { isMobile: true, component: CultureTheater, id: "cultureAndArtsMobileTheater2" },
];

const RELIGION_COMPONENTS: ShapeComponentEntry[] = [
  { isMobile: false, component: ReligionDove, id: "religionDesktopDoveSmall" },
  { isMobile: false, component: ReligionDove, id: "religionDesktopDoveLarge" },
  { isMobile: false, component: ReligionPray, id: "religionDesktopPray" },
  { isMobile: false, component: ReligionCandle, id: "religionDesktopCandleSmall" },
  { isMobile: false, component: ReligionCandle, id: "religionDesktopCandleLarge" },
  { isMobile: true, component: ReligionDove, id: "religionMobileDove" },
  { isMobile: true, component: ReligionPray, id: "religionMobilePray1" },
  { isMobile: true, component: ReligionPray, id: "religionMobilePray2" },
  { isMobile: true, component: ReligionCandle, id: "religionMobileCandle" },
];

const ENVIRONMENT_COMPONENTS: ShapeComponentEntry[] = [
  { isMobile: false, component: EnvironmentGlobe, id: "environmentDesktopGlobe" },
  { isMobile: false, component: EnvironmentLeaf, id: "environmentDesktopLeaf1" },
  { isMobile: false, component: EnvironmentLeaf, id: "environmentDesktopLeaf2" },
  { isMobile: false, component: EnvironmentTree, id: "environmentDesktopTree1" },
  { isMobile: false, component: EnvironmentTree, id: "environmentDesktopTree2" },
  { isMobile: true, component: EnvironmentGlobe, id: "environmentMobileGlobe" },
  { isMobile: true, component: EnvironmentLeaf, id: "environmentMobileLeaf1" },
  { isMobile: true, component: EnvironmentLeaf, id: "environmentMobileLeaf2" },
  { isMobile: true, component: EnvironmentTree, id: "environmentMobileTree" },
];

const GOLF_COMPONENTS: ShapeComponentEntry[] = [
  { isMobile: false, component: GolfGreen, id: "golfDesktopGreenLarge" },
  { isMobile: false, component: GolfGreen, id: "golfDesktopGreenSmall" },
  { isMobile: false, component: GolfBall, id: "golfDesktopBall" },
  { isMobile: false, component: GolfClub, id: "golfDesktopClub1" },
  { isMobile: false, component: GolfClub, id: "golfDesktopClub2" },
  { isMobile: true, component: GolfClub, id: "golfMobileClub" },
  { isMobile: true, component: GolfGreen, id: "golfMobileGreenLarge" },
  { isMobile: true, component: GolfGreen, id: "golfMobileGreenSmall" },
  { isMobile: true, component: GolfBall, id: "golfMobileBall" },
];

const HEALTH_COMPONENTS: ShapeComponentEntry[] = [
  { isMobile: false, component: HealthHeart, id: "healthDesktopHeartTopLeft" },
  { isMobile: false, component: HealthHeart, id: "healthDesktopHeartBottomRight" },
  { isMobile: false, component: HealthMentalHealth, id: "healthDesktopMentalHealth" },
  { isMobile: false, component: HealthCross, id: "healthDesktopHealth" },
  { isMobile: false, component: HealthStethoscope, id: "healthDesktopStethoscope" },
  { isMobile: true, component: HealthCross, id: "healthMobileHealth" },
  { isMobile: true, component: HealthHeart, id: "healthMobileHeart" },
  { isMobile: true, component: HealthMentalHealth, id: "healthMobileMentalHealth" },
  { isMobile: true, component: HealthStethoscope, id: "healthMobileStethoscope" },
];

const HUMANITARIAN_COMPONENTS: ShapeComponentEntry[] = [
  { isMobile: false, component: HumanitarianAid, id: "humanitarianDesktopAid" },
  { isMobile: false, component: HumanitarianGlobe, id: "humanitarianDesktopGlobe1" },
  { isMobile: false, component: HumanitarianGlobe, id: "humanitarianDesktopGlobe2" },
  { isMobile: false, component: HumanitarianHelping, id: "humanitarianDesktopHelpingTopRight" },
  { isMobile: false, component: HumanitarianHelping, id: "humanitarianDesktopHelpingBottomLeft" },
  { isMobile: true, component: HumanitarianGlobe, id: "humanitarianMobileGlobe1" },
  { isMobile: true, component: HumanitarianHelping, id: "humanitarianMobileHelping" },
  { isMobile: true, component: HumanitarianAid, id: "humanitarianMobileAid" },
  { isMobile: true, component: HumanitarianGlobe, id: "humanitarianMobileGlobe2" },
];

const THEME_COMPONENTS: Record<string, ShapeComponentEntry[]> = {
  [FormBackgroundTheme.Animal]: ANIMAL_COMPONENTS,
  [FormBackgroundTheme.Education]: EDUCATION_COMPONENTS,
  [FormBackgroundTheme.CultureAndArts]: CULTURE_AND_ARTS_COMPONENTS,
  [FormBackgroundTheme.Religion]: RELIGION_COMPONENTS,
  [FormBackgroundTheme.Environment]: ENVIRONMENT_COMPONENTS,
  [FormBackgroundTheme.Golf]: GOLF_COMPONENTS,
  [FormBackgroundTheme.Health]: HEALTH_COMPONENTS,
  [FormBackgroundTheme.Humanitarian]: HUMANITARIAN_COMPONENTS,
};

function getShapeComponents(theme: FormBackgroundTheme | null): ShapeComponentEntry[] {
  if (theme && THEME_COMPONENTS[theme]) {
    return THEME_COMPONENTS[theme];
  }
  return ABSTRACT_COMPONENTS;
}
